import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import XGSNavLink from "../xgs-nav-link/xgsNavLink";
import Loading from "../loading/loading";
import { Routes } from "../../app/route/RoutesConfig";
import { userSelector } from "../../slices/user/userSlice";
import { EquipmentFormModel } from "../../app/data/equipment/models";
import { useEquipmentByUnitNumber } from "./useEquipmentByUnitNumber";
import "./equipmentCard.scss";

export interface EquipmentCardProps {
  /** Equipment unit number (scanning equipmentId). */
  unitNumber: string;
  showHoverCard?: boolean;
  /** When false, hover popover can extend outside scroll containers (e.g. tables). */
  keepTooltipInside?: boolean;
  /** Navigate to Equipment master list on click / footer link. Default true. */
  linkToList?: boolean;
  className?: string;
}

interface PopoverContentProps {
  unitNumber: string;
  equipment: EquipmentFormModel | null | undefined;
  isLoading: boolean;
  isError: boolean;
  equipmentListPath: string;
  linkToList: boolean;
}

const PopoverContent: React.FC<PopoverContentProps> = ({
  unitNumber,
  equipment,
  isLoading,
  isError,
  equipmentListPath,
  linkToList,
}) => {
  const type = equipment?.equipmentType;
  const status = equipment?.status;
  const domicile = equipment?.domicileTerminal;
  const location = equipment?.location;
  const makeModel = [equipment?.make, equipment?.model].filter(Boolean).join(" ");
  const hasDetails = !!(type || status || domicile || location || makeModel);

  return (
    <div className="xgs-equipment-card-popover__content">
      <div className="xgs-equipment-card-popover__header">
        <span className="xgs-equipment-card-popover__title">{unitNumber}</span>
        {type && (
          <span className="xgs-equipment-card-popover__subtitle">{type}</span>
        )}
      </div>
      <div className="xgs-equipment-card-popover__divider" />
      {isLoading && !equipment ? (
        <div className="xgs-equipment-card-popover__loading">
          <Loading isLoading={true} />
        </div>
      ) : isError ? (
        <div className="xgs-equipment-card-popover__loading">
          Unable to load equipment details.
        </div>
      ) : !hasDetails ? (
        <div className="xgs-equipment-card-popover__loading">
          No equipment details found for this unit number.
        </div>
      ) : (
        <div className="xgs-equipment-card-popover__body">
          {status && (
            <>
              <span className="xgs-equipment-card-popover__label">Status</span>
              <span className="xgs-equipment-card-popover__value">{status}</span>
            </>
          )}
          {type && (
            <>
              <span className="xgs-equipment-card-popover__label">Type</span>
              <span className="xgs-equipment-card-popover__value">{type}</span>
            </>
          )}
          {makeModel && (
            <>
              <span className="xgs-equipment-card-popover__label">Make / Model</span>
              <span className="xgs-equipment-card-popover__value">{makeModel}</span>
            </>
          )}
          {domicile && (
            <>
              <span className="xgs-equipment-card-popover__label">Domicile</span>
              <span className="xgs-equipment-card-popover__value">{domicile}</span>
            </>
          )}
          {location && (
            <>
              <span className="xgs-equipment-card-popover__label">Location</span>
              <span className="xgs-equipment-card-popover__value">{location}</span>
            </>
          )}
        </div>
      )}
      {linkToList && (
        <div className="xgs-equipment-card-popover__footer">
          <XGSNavLink className="xgs-equipment-card-popover__more-link" to={equipmentListPath}>
            View equipment →
          </XGSNavLink>
        </div>
      )}
    </div>
  );
};

const EquipmentCard: React.FC<EquipmentCardProps> = ({
  unitNumber,
  showHoverCard = true,
  keepTooltipInside = false,
  linkToList = true,
  className,
}) => {
  const history = useHistory();
  const userState = useSelector(userSelector);
  const [shouldFetch, setShouldFetch] = useState(false);

  const { data: equipment, isLoading, isError } = useEquipmentByUnitNumber(
    unitNumber,
    shouldFetch
  );

  const terminalPath = userState.activeTerminal?.id ? `/${userState.activeTerminal.id}` : "";
  const equipmentListPath = `${terminalPath}${Routes.equipment.list}`;

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!linkToList) return;
    history.push(equipmentListPath);
  };

  const trigger = (
    <span
      className={`xgs-equipment-card${linkToList ? " xgs-equipment-card--clickable" : ""}${
        className ? ` ${className}` : ""
      }`}
      tabIndex={0}
      role={linkToList ? "link" : undefined}
      onClick={handleTriggerClick}
      onKeyDown={(e) => {
        if (linkToList && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleTriggerClick(e as unknown as React.MouseEvent);
        }
      }}
    >
      <span className="xgs-equipment-card__unit">{unitNumber}</span>
    </span>
  );

  if (!showHoverCard) return trigger;

  return (
    <Popup
      trigger={trigger}
      position={["bottom left", "bottom right", "top left"]}
      on={["hover", "focus"]}
      arrow={false}
      mouseEnterDelay={150}
      mouseLeaveDelay={120}
      keepTooltipInside={keepTooltipInside}
      onOpen={() => setShouldFetch(true)}
      contentStyle={{
        width: "296px",
        padding: 0,
        borderRadius: 4,
        border: "1px solid #E0E0E0",
        boxShadow: "0px 1px 6px rgba(0, 0, 0, 0.15)",
        zIndex: keepTooltipInside ? 22 : 1100,
      }}
    >
      <PopoverContent
        unitNumber={unitNumber}
        equipment={equipment}
        isLoading={isLoading}
        isError={isError}
        equipmentListPath={equipmentListPath}
        linkToList={linkToList}
      />
    </Popup>
  );
};

export default EquipmentCard;
